import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { useLanguage } from '../i18n';
import Modal from './ui/Modal';

/**
 * Shown when an upload is blocked because a document with the same name already
 * exists (Global Duplicate Filename Prevention ticket). Lets the user pick a
 * unique name and retry, or cancel. Reused by the chat-drop and file-manager
 * upload paths.
 *
 * Props:
 *   filename  - the conflicting name (input is pre-filled with it)
 *   message   - the backend's notice ("A document with this name already exists…")
 *   onSubmit(newName) - retry with the chosen name
 *   onCancel()        - abandon the upload
 */
const RenameDuplicateModal = ({ filename, message, onSubmit, onCancel }) => {
    const { t } = useLanguage();
    const [name, setName] = useState(filename || '');
    const inputRef = useRef(null);

    useEffect(() => {
        setName(filename || '');
        // Focus + select the base name (before the extension) for quick editing.
        const id = setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                const dot = (filename || '').lastIndexOf('.');
                inputRef.current.setSelectionRange(0, dot > 0 ? dot : (filename || '').length);
            }
        }, 0);
        return () => clearTimeout(id);
    }, [filename]);

    const trimmed = name.trim();
    const canSave = !!trimmed && trimmed !== (filename || '').trim();

    const submit = () => { if (canSave) onSubmit(trimmed); };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); submit(); }
        else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
    };

    return (
        <Modal onClose={onCancel} zIndex="z-[70]" cardClassName="p-5">
                <div className="flex items-start gap-3 mb-3">
                    <div className="mt-0.5 text-amber-500"><AlertCircle size={20} /></div>
                    <div className="flex-1">
                        <h2 className="text-base font-semibold text-white">{t('fmDuplicateName', 'Duplicate name')}</h2>
                        <p className="text-sm text-pai-meta mt-1">
                            {message || t('fmDuplicateNameMessage', 'A document with this name already exists. Please provide a unique name.')}
                        </p>
                    </div>
                    <button onClick={onCancel} className="p-1 text-pai-faint hover:text-white " title={t('commonCancel', 'Cancel')}>
                        <X size={18} />
                    </button>
                </div>

                <label className="block text-xs font-medium text-pai-meta mb-1">{t('fmNewName', 'New name')}</label>
                <input
                    ref={inputRef}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={t('fmEnterUniqueDocName', 'Enter a unique document name')}
                    className="w-full px-3 py-2 border border-pai rounded-lg bg-white/[0.06] text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <div className="flex items-center justify-end gap-2 mt-4">
                    <button
                        onClick={onCancel}
                        className="px-3 py-1.5 text-sm border border-pai rounded-lg text-pai-secondary hover:bg-white/[0.07]"
                    >
                        {t('commonCancel', 'Cancel')}
                    </button>
                    <button
                        onClick={submit}
                        disabled={!canSave}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {t('fmSaveWithThisName', 'Save with this name')}
                    </button>
                </div>
            </Modal>
    );
};

export default RenameDuplicateModal;
